/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import SmartLogOnHeader from "../SmartLogOnHeader/SmartLogOnHeader";
import {ExdsButton, ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from "@exds/core";
import './TestDevice.scss';
import React, {useContext, useEffect, useState} from "react";
import AppContext from "../../../../AppContext";
import appleIcon from '../../../../assets/images/Apple_Icon.png';
import androidIcon from '../../../../assets/images/Android_Icon.png';
import TestDeviceModal from "./TestDeviceModal";
import {useNavigate} from 'react-router-dom';
import {apiGet} from "../../../../service/service";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function TestDevice({setPageName}) {
  const {mobileDeviceContext, loadAA, sidContext} = useContext(AppContext);
  const mobileDevice = mobileDeviceContext.at(0);
  const sid = sidContext.at(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState('');
  const [nonce, setNonce] = useState('');
  const [fayeUrl, setFayeUrl] = useState('');
  const [channel, setChannel] = useState('');
  const [region, setRegion] = useState('');
  const pageName = 'Mobile App Setup | Smart Log On Test Device';

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const deviceContent = {
    'IPHONE': {
      iconSrc: appleIcon,
      altText: 'Apple icon',
      deviceName: 'iPhone'
    },
    'ANDROID': {
      iconSrc: androidIcon,
      altText: 'Android icon',
      deviceName: 'Android'
    }
  };

  useEffect(() => {
    if(!isModalOpen) {
      setQrCode('');
      setNonce('');
      setFayeUrl('');
      setChannel('');
    }
  }, [isModalOpen]);

  const handleTestDeviceClick = () => {
    apiGet('/api/smartLogOn/config')
      .then(async (response) => {
        const generateNonce = () => {
          const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
          const randomArray = new Uint8Array(256);
          crypto.getRandomValues(randomArray);
          return randomArray.reduce((acc, number) => acc + chars[number % chars.length]);
        };

        const SHA256 = (nonce) => {
          // Convert the string to a Uint8Array
          const data = new TextEncoder().encode(nonce);
          // Hash the data using SHA-256
          return crypto.subtle.digest('SHA-256', data);
        };

        const urlSafeSHA256 = async (nonce) => {
          const hashBuffer = await SHA256(nonce);
          // Convert the hash buffer to a base64 encoded string
          const base64String = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
          // Replace characters to make it URL safe and removing base64 padding from the end
          return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/\=+$/, '');  // eslint-disable-line no-useless-escape
        };

        const hash = (host) => {
          const utf8 = new TextEncoder().encode(host);
          return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray
              .map((bytes) => bytes.toString(16).padStart(2, '0'))
              .join('');
          });
        };

        const getFayeRegion = async () => {
          const fayeResponse = await fetch(response.authenticationFayeUrl + '/healthcheck', { method: 'HEAD' });
          // Retrieve the X-Region header
          return fayeResponse.headers.get('X-Region');
        };

        const nonce = generateNonce();
        const channel = await urlSafeSHA256(nonce);
        const hostname = await hash(response.authenticationHost || window.location.hostname);
        const region = await getFayeRegion();

        const qrcodeJSON = '{ "app_uid":"' + response.authenticationUid + '", "channel":"' + channel + '", "hostname":"' + hostname +'", "region":"' + region + '" }';        const qrCodeUrl = response.authenticationQrCodeUrl + "/app/webqr?" + window.btoa(qrcodeJSON);
        setQrCode(qrCodeUrl);
        setFayeUrl(response.authenticationFayeUrl);
        setNonce(nonce);
        setChannel(channel);
        setRegion(region);
      });

    setIsModalOpen(true);
  };

  const handleFinish = () => {
    navigate('/finish');
  };

  return (
    <ExdsGrid columnSpan={8} className='step-details'>
      <SmartLogOnHeader />

      <ExdsSpacing margin={{top: '1rem'}} >
        <ExdsText textStyle="titleLarge">
          Device registration successful
        </ExdsText>
      </ExdsSpacing>
      <ExdsSpacing margin={{top: '1rem'}} >
        <ExdsText textStyle="bodyLarge" className="spacing-016">
          You're all set up to use Smart Log On on the following device:
        </ExdsText>
      </ExdsSpacing>

      <ExdsSpacing margin={{top: '1.25rem', left: '1rem'}} >
        <ExdsLayout justifyContent="flex-start">
          <ExdsLayout flexDirection="column" justifyContent="center" gap="0.5rem">
            <div className="device-container">
              <div className="device">
                <div className="screen">
                  <img src={deviceContent[mobileDevice].iconSrc} alt={deviceContent[mobileDevice].altText}/>
                </div>
              </div>
            </div>
            <ExdsText textStyle="bodyLargeHeavier">
              {deviceContent[mobileDevice].deviceName}
            </ExdsText>
            <ExdsSpacing margin={{top: '0.3rem', left: '0.25rem'}} >
              <ExdsButton
                variant="tertiary"
                text="Test device"
                onClick={() => handleTestDeviceClick()}
              />
            </ExdsSpacing>
          </ExdsLayout>
        </ExdsLayout>
      </ExdsSpacing>

      <ExdsSpacing margin={{top: '2.15rem'}} >
        <ExdsText textStyle="bodyLarge" className="spacing-016">
          You can manage your registered devices by visiting <b>go/smartlogon</b> from a browser window once you're logged in.
        </ExdsText>
      </ExdsSpacing>
      <ExdsSpacing margin={{top: '4.5rem'}} >
        <ExdsLayout justifyContent="flex-end">
          <ExdsButton text="Finish" className="action-button" onClick={handleFinish}/>
        </ExdsLayout>
      </ExdsSpacing>

      <TestDeviceModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        qrCode={qrCode}
        nonce={nonce}
        fayeUrl={fayeUrl}
        channel={channel}
        pageName={pageName}
        region={region}
      />
    </ExdsGrid>
  );
}
